import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import uuidv1 from 'uuid/v1';
import { connect } from 'react-redux';
import Block from '../../atoms/Block/Block';
import PathBlockContainerStyled from './PathBlockContainer.styled';
import { starCalculator } from '../../../helpers/starCalculator';
import { onBlocksMounted } from '../../../state-actions/onBlocksMounted';
import { onChangeBlockOrder } from '../../../state-actions/onChangeBlockOrder';
import { onCorrectBlockOrder } from '../../../state-actions/onCorrectBlockOrder';
import { saveGameResults } from '../../../state-actions/saveGameResults';
import { blockTransition } from '../../../state-actions/blockTransition';
import { checkArrayEquality } from '../../../helpers/checkArrayEquality';
import RoundResults from '../../organisms/RoundResults/RoundResults';
import GameOver from '../../organisms/GameOver/GameOver';

const mapStateToProps = state => ({
  settings: state.settings,
  blocksMounted: state.blocksMounted,
  correctBlockOrder: state.correctBlockOrder,
  roundWin: state.game.roundWin,
  shuffledBlockOrder: state.shuffledBlockOrder,
  blockSelected: state.blockSelected,
  blockTargeted: state.blockTargeted,
  changeStart: state.changeStart,
  started: state.game.started,
  initTime: state.gameData[state.saved.level].time,
  timeOut: state.game.timeOut,
  moveCount: state.game.moveCount,
  timeTaken: state.gameData[state.saved.level].time - state.game.time,
  timeTotal: state.game.timeTaken,
  savedTimeTaken: state.saved.best[state.saved.level].timeTaken
    ? state.saved.best[state.saved.level].timeTaken
    : 0,
  savedMoveCount: state.saved.best[state.saved.level].moveCount
    ? state.saved.best[state.saved.level].moveCount
    : 0,
  savedStarCount: state.saved.best[state.saved.level].starCount
    ? state.saved.best[state.saved.level].starCount
    : 0,
  countDown: state.settings.countDown,
  countUp: state.settings.countUp,
});

const mapDispatchToProps = dispatch => ({
  onBlocksMounted: status => dispatch(onBlocksMounted(status)),
  blockTransition: bool => dispatch(blockTransition(bool)),
  onChangeBlockOrder: array => dispatch(onChangeBlockOrder(array)),
  onCorrectBlockOrder: bool => dispatch(onCorrectBlockOrder(bool)),
  saveGameResults: obj => dispatch(saveGameResults(obj)),
});

class PathBlockContainer extends Component {
  constructor(props) {
    super(props);
    this.onBlocksMounted = props.onBlocksMounted;
  }

  componentDidMount() {
    const blocksMounted = true;
    this.onBlocksMounted(blocksMounted);
  }

  // Stop a rerender unless we have a target selected to swap with
  shouldComponentUpdate(nextProps) {
    const { shuffledBlockOrder, roundWin, started, timeOut } = nextProps;
    const newShuffledBlockOrder = [...shuffledBlockOrder];
    const prevShuffledBlockOrder = this.props.shuffledBlockOrder;
    const gameInPlay = started && started !== this.props.started; // Only updates on first game trigger

    if (Object.keys(nextProps.blockTargeted).length) {
      const {
        blockSelected,
        blockTargeted,
        onChangeBlockOrder,
        blockTransition,
      } = nextProps;
      blockTransition(true);
      newShuffledBlockOrder[blockTargeted.orderID - 1] = blockSelected.offset; // Swap to
      newShuffledBlockOrder[blockSelected.orderID - 1] = blockTargeted.offset; // Swap to
      onChangeBlockOrder(newShuffledBlockOrder);
      blockTransition(false);
    }

    // Update the blocks if any of the below are true
    if (
      !checkArrayEquality(shuffledBlockOrder, prevShuffledBlockOrder) ||
      roundWin ||
      timeOut ||
      gameInPlay
    )
      return true;
    return false;
  }

  componentDidUpdate() {
    const {
      shuffledBlockOrder,
      correctBlockOrder,
      onCorrectBlockOrder,
    } = this.props;
    if (checkArrayEquality(shuffledBlockOrder, correctBlockOrder)) {
      this.calcSavedGameResults();
      onCorrectBlockOrder(true);
    }
  }

  calcSavedGameResults() {
    const {
      initTime,
      saveGameResults,
      savedTimeTaken,
      timeTaken,
      timeTotal,
      savedMoveCount,
      moveCount,
      savedStarCount,
      countUp,
      countDown,
    } = this.props;

    const time = countUp ? timeTotal : countDown ? timeTaken : '-';

    const starCount = starCalculator(initTime, time, moveCount);

    // Update only if 'best' has improved
    saveGameResults({
      timeTaken: time <= savedTimeTaken ? time : savedTimeTaken,
      moveCount: moveCount <= savedMoveCount ? moveCount : savedMoveCount,
      starCount: starCount >= savedStarCount ? starCount : savedStarCount,
    });
  }

  generateBlocks(shuffle) {
    const {
      blocksMounted,
      shuffledBlockOrder,
      correctBlockOrder,
      roundWin,
    } = this.props;
    return correctBlockOrder.map((value, index) => (
      <Block
        orderID={index + 1}
        ready={blocksMounted}
        key={uuidv1()}
        offset={shuffle ? shuffledBlockOrder[index] : index}
        block={`block${shuffle ? shuffledBlockOrder[index] : index}`}
        roundWin={roundWin}
      />
    ));
  }

  render() {
    const { settings, roundWin, started, timeOut } = this.props;
    const { shuffle } = settings;
    return (
      <Fragment>
        {roundWin && <RoundResults show={roundWin} />}
        {timeOut && !roundWin && <GameOver show={timeOut} />}
        <PathBlockContainerStyled
          id="block-container"
          started={started}
          roundWin={roundWin}
        >
          {this.generateBlocks(shuffle)}
        </PathBlockContainerStyled>
      </Fragment>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PathBlockContainer);

PathBlockContainer.propTypes = {
  settings: PropTypes.shape({
    shuffle: PropTypes.bool,
    gameLevel: PropTypes.number,
  }).isRequired,
  correctBlockOrder: PropTypes.arrayOf(PropTypes.number).isRequired,
  shuffledBlockOrder: PropTypes.arrayOf(PropTypes.number).isRequired,
  blocksMounted: PropTypes.bool.isRequired,
  roundWin: PropTypes.bool,
  timeOut: PropTypes.bool.isRequired,
  onBlocksMounted: PropTypes.func.isRequired,
  blockSelected: PropTypes.objectOf(PropTypes.number),
  blockTargeted: PropTypes.objectOf(PropTypes.number),
  started: PropTypes.bool.isRequired,
  initTime: PropTypes.number.isRequired,
  timeTaken: PropTypes.number,
  timeTotal: PropTypes.number,
  savedTimeTaken: PropTypes.oneOfType([PropTypes.func, PropTypes.number])
    .isRequired,
  moveCount: PropTypes.number,
  savedMoveCount: PropTypes.oneOfType([PropTypes.func, PropTypes.number])
    .isRequired,
  savedStarCount: PropTypes.oneOfType([PropTypes.func, PropTypes.number])
    .isRequired,
  countUp: PropTypes.bool.isRequired,
  countDown: PropTypes.bool.isRequired,
};

PathBlockContainer.defaultProps = {
  blockSelected: {},
  blockTargeted: {},
  roundWin: false,
  timeTaken: 0,
  timeTotal: 0,
  moveCount: 0,
};



// WEBPACK FOOTER //
// ./src/components/molecules/PathBlockContainer/PathBlockContainer.js